import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import TemplateDirectory from './components/TemplateDirectory';
import TemplateUploadStep from './components/TemplateUploadStep';
import FieldMappingStep from './components/FieldMappingStep';
import {
  createTemplate,
  updateTemplate,
  publishTemplate,
  replaceTemplateHtml,
  downloadTemplateHtml,
  DocumentTemplateCategory,
} from '../../../api/General/documentTemplates';

const EMPTY_TEMPLATE_DATA = {
  templateId: null,
  uploadedTemplate: null,
  fileIsNew: false,
  templateName: '',
  category: DocumentTemplateCategory.Contract,
  description: '',
  originalFileName: '',
  templatePreview: null,
  detectedFields: [],
  fieldMappings: [],
};

const DocumentTemplateManager = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('directory'); // 'directory' | 'wizard'
  const [currentStep, setCurrentStep] = useState(1);
  const [templateData, setTemplateData] = useState(EMPTY_TEMPLATE_DATA);
  const [busy, setBusy] = useState(false);
  const [directoryNonce, setDirectoryNonce] = useState(0);
  const { toast, showToast, dismissToast } = useToast();

  // Prep-only flow: introduce the document, then map its placeholders. Publishing,
  // archiving and file replacement happen from the directory / on save.
  const steps = [
    { id: 1, title: t('Upload Template'), icon: 'Upload', description: t('Upload Word document template') },
    { id: 2, title: t('Map Fields'), icon: 'Link', description: t('Map employee data fields') },
  ];

  const updateTemplateData = useCallback((section, data) => {
    setTemplateData((prev) => ({ ...prev, [section]: data }));
  }, []);

  const backToDirectory = useCallback(() => {
    setMode('directory');
    setCurrentStep(1);
    setTemplateData(EMPTY_TEMPLATE_DATA);
    setDirectoryNonce((n) => n + 1);
  }, []);

  const handleCreateNew = useCallback(() => {
    setTemplateData(EMPTY_TEMPLATE_DATA);
    setCurrentStep(1);
    setMode('wizard');
  }, []);

  // Open the wizard pre-loaded from a saved template: load the stored HTML (the persisted
  // artifact) for preview, and pre-fill detected fields + saved mappings. Lands on Upload so
  // HR can review or replace the document before re-mapping.
  const handleEditTemplate = useCallback(async (template) => {
    setBusy(true);
    try {
      const html = await downloadTemplateHtml(template.id);

      setTemplateData({
        ...EMPTY_TEMPLATE_DATA,
        templateId: template.id,
        uploadedTemplate: null,
        fileIsNew: false,
        templateName: template.name,
        category: template.category,
        description: template.description || '',
        originalFileName: template.originalFileName || '',
        templatePreview: html || '',
        detectedFields: template.detectedFields || [],
        fieldMappings: template.fieldMappings || [],
      });
      setCurrentStep(1);
      setMode('wizard');
    } catch {
      showToast(t('Could not open this template'), 'error');
    } finally {
      setBusy(false);
    }
  }, [showToast, t]);

  // Step 1 → 2: persist the template. Creates a Draft on first save; replaces the stored
  // HTML when an existing template's document was swapped out. The HTML rendered from the
  // .docx (docx-preview) is what we persist — the .docx itself is never sent. Detected fields
  // always come back from the server (it re-scans the stored HTML).
  const handleAfterUpload = useCallback(async () => {
    // Nothing to persist on a fresh wizard with no document yet.
    if (!templateData.templateId && !templateData.uploadedTemplate) return;
    const originalFileName = templateData.uploadedTemplate?.name || templateData.originalFileName || '';
    setBusy(true);
    try {
      if (!templateData.templateId) {
        const result = await createTemplate({
          name: templateData.templateName || originalFileName,
          category: templateData.category,
          description: templateData.description || undefined,
          detectedFields: templateData.detectedFields || [],
          fieldMappings: [],
          html: templateData.templatePreview || '',
          originalFileName,
        });
        if (result.errors.length) {
          showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
          return;
        }
        setTemplateData((prev) => ({
          ...prev,
          templateId: result.payload.id,
          fileIsNew: false,
          detectedFields: result.payload.detectedFields || prev.detectedFields,
        }));
        showToast(t('Draft saved to your library'), 'success');
      } else if (templateData.fileIsNew) {
        const result = await replaceTemplateHtml(templateData.templateId, templateData.templatePreview || '', originalFileName);
        if (result.errors.length) {
          showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
          return;
        }
        setTemplateData((prev) => ({
          ...prev,
          fileIsNew: false,
          detectedFields: result.payload.detectedFields || prev.detectedFields,
        }));
        showToast(t('Template file replaced'), 'success');
      }
      setCurrentStep(2);
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setBusy(false);
    }
  }, [templateData, showToast, t]);

  // Step 2: persist mappings, optionally publish, then return to the directory.
  // `mappings` is passed straight from FieldMappingStep so we never read a stale
  // templateData.fieldMappings that hasn't absorbed the just-applied auto-mapping.
  const handleSaveMappings = useCallback(async (publish, mappings) => {
    if (!templateData.templateId) return;
    const fieldMappings = mappings ?? templateData.fieldMappings ?? [];
    setBusy(true);
    try {
      const updateResult = await updateTemplate({
        id: templateData.templateId,
        name: templateData.templateName,
        category: templateData.category,
        description: templateData.description || undefined,
        fieldMappings,
      });
      if (updateResult.errors.length) {
        showToast(updateResult.errors[0]?.description || t('Something went wrong'), 'error');
        return;
      }
      if (publish) {
        const pubResult = await publishTemplate(templateData.templateId);
        if (pubResult.errors.length) {
          showToast(pubResult.errors[0]?.description || t('Could not publish template'), 'error');
          return;
        }
        showToast(t('Template published'), 'success');
      } else {
        showToast(t('Draft saved'), 'success');
      }
      backToDirectory();
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setBusy(false);
    }
  }, [templateData, showToast, t, backToDirectory]);

  const handleStepClick = useCallback((stepId) => {
    if (stepId < currentStep) setCurrentStep(stepId);
  }, [currentStep]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <TemplateUploadStep data={templateData} onDataUpdate={updateTemplateData} onNext={handleAfterUpload} busy={busy} />;
      case 2:
        return (
          <FieldMappingStep
            data={templateData}
            onDataUpdate={updateTemplateData}
            onSaveDraft={(mappings) => handleSaveMappings(false, mappings)}
            onPublish={(mappings) => handleSaveMappings(true, mappings)}
            onPrev={() => setCurrentStep(1)}
            busy={busy}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <PageLayout
        title={t('Document Template Manager')}
        description={t('Prepare reusable document templates for employees to download on demand')}
        icon={FileText}
        accentColor="amber"
        maxWidth="1600px"
        headerActions={
          mode === 'directory' ? (
            <Button variant="outline" onClick={handleCreateNew} iconName="Plus" iconPosition="left">
              {t('Create Template')}
            </Button>
          ) : (
            <Button variant="outline" onClick={backToDirectory}>
              <Icon name="ArrowLeft" size={16} className="me-2 rtl:rotate-180" />
              {t('Back to Library')}
            </Button>
          )
        }
      >
        {mode === 'directory' ? (
          <TemplateDirectory
            onEdit={handleEditTemplate}
            onCreateNew={handleCreateNew}
            showToast={showToast}
            reloadNonce={directoryNonce}
          />
        ) : (
          <div className="space-y-6">
            {/* Progress Indicator */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <button
                      type="button"
                      className={`flex flex-col items-center text-center ${step.id <= currentStep ? 'opacity-100' : 'opacity-50'} ${
                        step.id < currentStep ? 'cursor-pointer' : 'cursor-default'
                      }`}
                      onClick={() => handleStepClick(step.id)}
                    >
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                          step.id === currentStep
                            ? 'bg-primary text-primary-foreground shadow-lg scale-110'
                            : step.id < currentStep
                            ? 'bg-green-500 text-white'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {step.id < currentStep ? <Icon name="Check" size={20} strokeWidth={3} /> : <Icon name={step.icon} size={20} />}
                      </div>
                      <span className={`text-sm font-medium ${step.id === currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {step.title}
                      </span>
                      <span className="text-xs text-muted-foreground mt-1 hidden sm:block">{step.description}</span>
                    </button>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 sm:mx-4 rounded transition-all ${step.id < currentStep ? 'bg-green-500' : 'bg-muted'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <div className="bg-card rounded-lg shadow-sm border border-border">{renderStepContent()}</div>
          </div>
        )}
      </PageLayout>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
};

export default DocumentTemplateManager;
